import { useEffect, useState } from 'react';
import { getCurrentUser } from '../../../services/authApi';
import { SessionManager } from '../../../utils/SessionManager';
import './VideoWatermark.css';

/**
 * 不正拡散の抑止とトレーサビリティ確保のための動画ウォーターマーク (#206)
 *
 * - 視聴者の氏名・メール・現在時刻を、ごく薄く・小さく常時表示する。
 * - 中央の術野を避け、上帯・下帯をランダムに巡回させる。下端固定だと「下を切り抜くだけ」で
 *   消せてしまうため、上下どちらにも出るようにして切り抜き（クロップ）を困難にする。
 * - 視聴の邪魔にならないよう、移動はゆっくり静かにスライドさせる。
 * - pointer-events / user-select を無効化し、操作の妨げや選択コピーを防ぐ。
 *
 * 視聴者情報の取得について:
 *   本アプリは AuthProvider をツリーにマウントしていないため useAuth() からは取得できない。
 *   ログイン済みの正本は /api/auth/me（getCurrentUser）であり、これを取得して表示する。
 *   取得に失敗した場合は SessionManager の名前にフォールバックする。
 *   未ログイン（トークン無し）の場合は表示しない。
 *
 * 管理者を含む全ロールで表示する（トレーサビリティ優先・CTO決定）。
 */

// 透かしの再配置間隔（ミリ秒）。長め＝視聴の邪魔を抑えつつ、全画面を巡回して切り抜き対策。
const REPOSITION_INTERVAL_MS = 18000;
// タイムスタンプの更新間隔（ミリ秒）。
const TIMESTAMP_INTERVAL_MS = 30000;

interface WatermarkIdentity {
  name: string;
  email: string;
}

interface WatermarkPosition {
  top: number;
  left: number;
}

const formatTimestamp = (date: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

// 中央の術野を避け、上帯または下帯にランダム配置する（横方向は広く巡回）。
// 上下どちらにも出るため、下端だけを切り抜いても消せない（切り抜き対策を維持）。
const randomPosition = (): WatermarkPosition => {
  const inTopBand = Math.random() < 0.5;
  const top = inTopBand ? 5 + Math.random() * 14 : 70 + Math.random() * 15;
  return {
    top,
    left: 4 + Math.random() * 62,
  };
};

const VideoWatermark = () => {
  const [identity, setIdentity] = useState<WatermarkIdentity | null>(null);
  const [position, setPosition] = useState<WatermarkPosition>(randomPosition);
  const [timestamp, setTimestamp] = useState<string>(() => formatTimestamp(new Date()));

  // 視聴者情報を取得する（/api/auth/me、失敗時は SessionManager にフォールバック）。
  useEffect(() => {
    let cancelled = false;

    const resolveIdentity = async (): Promise<void> => {
      // 未ログイン時は表示しない。
      if (!SessionManager.getToken()) {
        return;
      }
      try {
        const profile = await getCurrentUser();
        if (!cancelled && profile && (profile.name || profile.email)) {
          setIdentity({ name: profile.name ?? '', email: profile.email ?? '' });
          return;
        }
      } catch {
        // フォールバックへ進む。
      }
      if (!cancelled) {
        const session = SessionManager.getActiveSession();
        if (session?.name) {
          setIdentity({ name: session.name, email: '' });
        }
      }
    };

    void resolveIdentity();
    return () => {
      cancelled = true;
    };
  }, []);

  // 位置をランダムに変動させる（全画面を巡回・切り抜き対策）。
  useEffect(() => {
    const intervalId = setInterval(() => {
      setPosition(randomPosition());
    }, REPOSITION_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  // タイムスタンプを定期的に更新する。
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimestamp(formatTimestamp(new Date()));
    }, TIMESTAMP_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  if (!identity) {
    return null;
  }

  const identityLabel = [identity.name, identity.email]
    .filter(Boolean)
    .join(' / ');

  return (
    <div
      className="video-watermark"
      style={{ top: `${position.top}%`, left: `${position.left}%` }}
      aria-hidden="true"
      data-testid="video-watermark"
    >
      <span className="video-watermark__line">{identityLabel}</span>
      <span className="video-watermark__line">{timestamp}</span>
    </div>
  );
};

export default VideoWatermark;
